/**
 * Drivers Management Page
 * =======================
 * صفحة إدارة السواقين
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import SAR from '../../../components/SAR';
import { useDialog } from '../../../context/DialogContext';

const AVAILABILITY = {
  online:   { label: 'متاح',     color: '#22c55e', bg: '#dcfce7' },
  busy:     { label: 'مشغول',    color: '#f59e0b', bg: '#fef3c7' },
  on_break: { label: 'استراحة', color: '#6366f1', bg: '#ede9fe' },
  offline:  { label: 'غير متاح', color: '#94a3b8', bg: '#f1f5f9' },
};

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  driver: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  wifi: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>,
};

const DriversPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [updatingAvailability, setUpdatingAvailability] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const [formData, setFormData] = useState({
    // البيانات الشخصية
    full_name: '',
    full_name_ar: '',
    phone: '',
    email: '',
    date_of_birth: '',
    nationality: '',
    national_id: '',
    national_id_expiry: '',
    address: '',
    city: '',
    // الرخصة والشهادات
    driver_license_number: '',
    driver_license_type: '',
    driver_license_expiry: '',
    health_certificate_number: '',
    health_certificate_expiry: '',
    // بيانات التوظيف
    status: 'pending',
    hire_date: '',
    contract_type: '',
    salary_type: 'monthly',
    base_salary: '',
    commission_per_order: '',
    commission_percentage: '',
    // البيانات البنكية
    bank_name: '',
    bank_account: '',
    iban: '',
    // جهة الاتصال الطوارئ
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });

  const emptyForm = {
    full_name: '', full_name_ar: '', phone: '', email: '', date_of_birth: '', nationality: '',
    national_id: '', national_id_expiry: '', address: '', city: '',
    driver_license_number: '', driver_license_type: '', driver_license_expiry: '',
    health_certificate_number: '', health_certificate_expiry: '',
    status: 'pending', hire_date: '', contract_type: '', salary_type: 'monthly',
    base_salary: '', commission_per_order: '', commission_percentage: '',
    bank_name: '', bank_account: '', iban: '',
    emergency_contact_name: '', emergency_contact_phone: ''
  };

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('status', filter);
      const data = await apiCall(`/tenant/drivers?${params}`);
      setDrivers(data.items || []);
    } catch (err) {
      showError('فشل تحميل السواقين');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, filter, showError]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Collect files to upload
      const filesToUpload = [];
      
      // تنظيف البيانات الفارغة
      const cleanData = Object.fromEntries(
        Object.entries(formData).filter(([k, v]) => {
          if (k.endsWith('_file') && v instanceof File) {
            filesToUpload.push({ fieldKey: k.replace('_file', ''), file: v });
            return false;
          }
          return v !== '' && v !== null && !(v instanceof File);
        })
      );
      
      let driverId;
      if (editingDriver) {
        await apiCall(`/tenant/drivers/${editingDriver.id}`, { method: 'PUT', body: JSON.stringify(cleanData) });
        driverId = editingDriver.id;
        success('تم تحديث السائق بنجاح');
      } else {
        const result = await apiCall('/tenant/drivers', { method: 'POST', body: JSON.stringify(cleanData) });
        driverId = result.id;
        success('تم إضافة السائق بنجاح');
      }
      
      // Upload files
      if (filesToUpload.length > 0 && driverId) {
        const token = localStorage.getItem('watrly_tenant_token');
        for (const { fieldKey, file } of filesToUpload) {
          const uploadForm = new FormData();
          uploadForm.append('file', file);
          uploadForm.append('driver_id', driverId);
          uploadForm.append('document_type', fieldKey);
          
          await fetch('http://localhost:8002/api/v1/tenant/uploads/driver-document', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: uploadForm
          });
        }
      }
      
      setShowModal(false);
      setEditingDriver(null);
      setFormData(emptyForm);
      setActiveSection('personal');
      loadDrivers();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      full_name: driver.full_name || '',
      full_name_ar: driver.full_name_ar || '',
      phone: driver.phone || '',
      email: driver.email || '',
      date_of_birth: driver.date_of_birth || '',
      nationality: driver.nationality || '',
      national_id: driver.national_id || '',
      national_id_expiry: driver.national_id_expiry || '',
      address: driver.address || '',
      city: driver.city || '',
      driver_license_number: driver.driver_license_number || '',
      driver_license_type: driver.driver_license_type || '',
      driver_license_expiry: driver.driver_license_expiry || '',
      health_certificate_number: driver.health_certificate_number || '',
      health_certificate_expiry: driver.health_certificate_expiry || '',
      status: driver.status || 'pending',
      hire_date: driver.hire_date || '',
      contract_type: driver.contract_type || '',
      salary_type: driver.salary_type || 'monthly',
      base_salary: driver.base_salary || '',
      commission_per_order: driver.commission_per_order || '',
      commission_percentage: driver.commission_percentage || '',
      bank_name: driver.bank_name || '',
      bank_account: driver.bank_account || '',
      iban: driver.iban || '',
      emergency_contact_name: driver.emergency_contact_name || '',
      emergency_contact_phone: driver.emergency_contact_phone || ''
    });
    setActiveSection('personal');
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingDriver(null);
    setFormData(emptyForm);
    setActiveSection('personal');
    setShowModal(true);
  };

  const handleAvailabilityChange = async (driverId, newStatus, e) => {
    e.stopPropagation();
    setUpdatingAvailability(driverId);
    try {
      await apiCall(`/tenant/drivers/${driverId}/availability`, {
        method: 'PATCH',
        body: JSON.stringify({ availability_status: newStatus })
      });
      setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, availability_status: newStatus } : d));
      success('تم تحديث الحالة');
    } catch (err) {
      showError('فشل تحديث الحالة');
    } finally {
      setUpdatingAvailability(null);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'حذف السائق', message: 'هل أنت متأكد من حذف هذا السائق؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/drivers/${id}`, { method: 'DELETE' });
      success('تم حذف السائق بنجاح');
      loadDrivers();
    } catch (err) {
      showError('فشل حذف السائق');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة السواقين</h1>
          <p>{drivers.length} سائق</p>
        </div>
        <div className="header-actions">
          <button className="btn btn--secondary" onClick={() => navigate('/tenant/drivers/pending')}>
            طلبات الموافقة
          </button>
          <button className="btn btn--secondary" onClick={() => navigate('/tenant/drivers/map')}>
            {I.map} الخريطة
          </button>
          <button className="btn btn--primary" onClick={openAddModal}>
            {I.plus} <span>إضافة سائق</span>
          </button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-box">
          {I.search}
          <input type="text" placeholder="بحث بالاسم أو الهاتف..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">جميع الحالات</option>
          <option value="active">نشط</option>
          <option value="inactive">غير نشط</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : drivers.length === 0 ? (
        <div className="page-empty">{I.driver}<h3>لا يوجد سواقين</h3><p>ابدأ بإضافة سواقين جدد</p></div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>السائق</th>
                <th>الهاتف</th>
                <th>التقييم</th>
                <th>الطلبات</th>
                <th>المركبة</th>
                <th>الإتاحة</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map(d => (
                <tr key={d.id} onClick={() => navigate(`/tenant/drivers/${d.id}`)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="cell-user">
                      <div className="cell-user__avatar">{d.full_name?.charAt(0)}</div>
                      <div className="cell-user__info">
                        <span className="cell-user__name">{d.full_name}</span>
                        <span className="cell-user__email">{d.email || '-'}</span>
                      </div>
                    </div>
                  </td>
                  <td dir="ltr">{d.phone}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b' }}>
                      {I.star}
                      <span>{d.rating_avg?.toFixed(1) || '-'}</span>
                    </div>
                  </td>
                  <td>{d.total_orders || 0}</td>
                  <td>{d.vehicle?.plate || '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      value={d.availability_status || 'offline'}
                      disabled={updatingAvailability === d.id}
                      onChange={(e) => handleAvailabilityChange(d.id, e.target.value, e)}
                      style={{
                        padding: '4px 8px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                        background: AVAILABILITY[d.availability_status]?.bg || '#f1f5f9',
                        color: AVAILABILITY[d.availability_status]?.color || '#94a3b8'
                      }}
                    >
                      {Object.entries(AVAILABILITY).map(([val, info]) => (
                        <option key={val} value={val}>{info.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`badge badge--${d.status === 'active' ? 'success' : d.status === 'suspended' ? 'warning' : 'danger'}`}>
                      {d.status === 'active' ? 'نشط' : d.status === 'suspended' ? 'معلق' : 'غير نشط'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleEdit(d); }}>{I.edit}</button>
                      <button className="action-btn action-btn--danger" onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}>{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal--large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDriver ? 'تعديل سائق' : 'إضافة سائق جديد'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Section Tabs */}
              <div className="form-tabs">
                <button type="button" className={`form-tab ${activeSection === 'personal' ? 'form-tab--active' : ''}`} onClick={() => setActiveSection('personal')}>البيانات الشخصية</button>
                <button type="button" className={`form-tab ${activeSection === 'license' ? 'form-tab--active' : ''}`} onClick={() => setActiveSection('license')}>الرخصة والشهادات</button>
                <button type="button" className={`form-tab ${activeSection === 'employment' ? 'form-tab--active' : ''}`} onClick={() => setActiveSection('employment')}>بيانات التوظيف</button>
                <button type="button" className={`form-tab ${activeSection === 'bank' ? 'form-tab--active' : ''}`} onClick={() => setActiveSection('bank')}>البيانات البنكية</button>
                <button type="button" className={`form-tab ${activeSection === 'emergency' ? 'form-tab--active' : ''}`} onClick={() => setActiveSection('emergency')}>جهة الطوارئ</button>
              </div>

              <div className="modal-body">
                {/* البيانات الشخصية */}
                {activeSection === 'personal' && (
                  <div className="form-grid">
                    <div className="form-group"><label>الاسم الكامل (إنجليزي) *</label><input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required placeholder="Full Name" /></div>
                    <div className="form-group"><label>الاسم الكامل (عربي)</label><input type="text" value={formData.full_name_ar} onChange={(e) => setFormData({...formData, full_name_ar: e.target.value})} placeholder="الاسم الكامل" /></div>
                    <div className="form-group"><label>رقم الهاتف *</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required placeholder="05xxxxxxxx" dir="ltr" /></div>
                    <div className="form-group"><label>البريد الإلكتروني</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@example.com" dir="ltr" /></div>
                    <div className="form-group"><label>تاريخ الميلاد</label><input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} /></div>
                    <div className="form-group"><label>الجنسية</label>
                      <select value={formData.nationality} onChange={(e) => setFormData({...formData, nationality: e.target.value})}>
                        <option value="">-- اختر الجنسية --</option>
                        <option value="SA">سعودي</option>
                        <option value="YE">يمني</option>
                        <option value="PK">باكستاني</option>
                        <option value="BD">بنغلاديشي</option>
                        <option value="IN">هندي</option>
                        <option value="PH">فلبيني</option>
                        <option value="EG">مصري</option>
                        <option value="SY">سوري</option>
                        <option value="SD">سوداني</option>
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    <div className="form-group"><label>رقم الهوية/الإقامة *</label><input type="text" value={formData.national_id} onChange={(e) => setFormData({...formData, national_id: e.target.value})} required placeholder="10 أرقام" /></div>
                    <div className="form-group"><label>تاريخ انتهاء الهوية</label><input type="date" value={formData.national_id_expiry} onChange={(e) => setFormData({...formData, national_id_expiry: e.target.value})} /></div>
                    <div className="form-group"><label>العنوان</label><input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="العنوان التفصيلي" /></div>
                    <div className="form-group"><label>المدينة</label>
                      <select value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})}>
                        <option value="">-- اختر المدينة --</option>
                        <option value="الرياض">الرياض</option>
                        <option value="جدة">جدة</option>
                        <option value="مكة">مكة</option>
                        <option value="المدينة">المدينة</option>
                        <option value="الدمام">الدمام</option>
                        <option value="الخبر">الخبر</option>
                        <option value="الأحساء">الأحساء</option>
                        <option value="القطيف">القطيف</option>
                        <option value="أخرى">أخرى</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* الرخصة والشهادات */}
                {activeSection === 'license' && (
                  <div className="form-grid">
                    <div className="form-group"><label>رقم رخصة القيادة *</label><input type="text" value={formData.driver_license_number} onChange={(e) => setFormData({...formData, driver_license_number: e.target.value})} required placeholder="رقم الرخصة" /></div>
                    <div className="form-group"><label>نوع الرخصة</label>
                      <select value={formData.driver_license_type} onChange={(e) => setFormData({...formData, driver_license_type: e.target.value})}>
                        <option value="">-- اختر النوع --</option>
                        <option value="private">خصوصي</option>
                        <option value="public_light">عمومي خفيف</option>
                        <option value="public_heavy">عمومي ثقيل</option>
                        <option value="truck">شاحنات</option>
                        <option value="tanker">صهاريج</option>
                      </select>
                    </div>
                    <div className="form-group"><label>تاريخ انتهاء الرخصة</label><input type="date" value={formData.driver_license_expiry} onChange={(e) => setFormData({...formData, driver_license_expiry: e.target.value})} /></div>
                    <div className="form-group"><label>رقم الشهادة الصحية</label><input type="text" value={formData.health_certificate_number} onChange={(e) => setFormData({...formData, health_certificate_number: e.target.value})} placeholder="رقم الشهادة" /></div>
                    <div className="form-group"><label>تاريخ انتهاء الشهادة الصحية</label><input type="date" value={formData.health_certificate_expiry} onChange={(e) => setFormData({...formData, health_certificate_expiry: e.target.value})} /></div>
                    <div className="form-group">
                      <label>صورة رخصة القيادة</label>
                      <input type="file" accept="image/*,.pdf"
                        onChange={(e) => setFormData({...formData, driver_license_image_file: e.target.files[0] || null})}
                        style={{padding:'6px', border:'1px dashed #ccc', borderRadius:'6px', width:'100%'}} />
                      {formData.driver_license_image_file && <small style={{color:'green'}}>✓ {formData.driver_license_image_file.name}</small>}
                    </div>
                    <div className="form-group">
                      <label>صورة الهوية/الإقامة</label>
                      <input type="file" accept="image/*,.pdf"
                        onChange={(e) => setFormData({...formData, national_id_image_file: e.target.files[0] || null})}
                        style={{padding:'6px', border:'1px dashed #ccc', borderRadius:'6px', width:'100%'}} />
                      {formData.national_id_image_file && <small style={{color:'green'}}>✓ {formData.national_id_image_file.name}</small>}
                    </div>
                    <div className="form-group">
                      <label>الشهادة الصحية (ملف)</label>
                      <input type="file" accept="image/*,.pdf"
                        onChange={(e) => setFormData({...formData, health_certificate_image_file: e.target.files[0] || null})}
                        style={{padding:'6px', border:'1px dashed #ccc', borderRadius:'6px', width:'100%'}} />
                      {formData.health_certificate_image_file && <small style={{color:'green'}}>✓ {formData.health_certificate_image_file.name}</small>}
                    </div>
                  </div>
                )}

                {/* بيانات التوظيف */}
                {activeSection === 'employment' && (
                  <div className="form-grid">
                    <div className="form-group"><label>حالة السائق</label>
                      <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                        <option value="pending">قيد المراجعة</option>
                        <option value="active">نشط</option>
                        <option value="suspended">معلق</option>
                        <option value="blocked">محظور</option>
                      </select>
                    </div>
                    <div className="form-group"><label>تاريخ التوظيف</label><input type="date" value={formData.hire_date} onChange={(e) => setFormData({...formData, hire_date: e.target.value})} /></div>
                    <div className="form-group"><label>نوع العقد</label>
                      <select value={formData.contract_type} onChange={(e) => setFormData({...formData, contract_type: e.target.value})}>
                        <option value="">-- اختر النوع --</option>
                        <option value="full_time">دوام كامل</option>
                        <option value="part_time">دوام جزئي</option>
                        <option value="contractor">مقاول</option>
                        <option value="freelance">حر</option>
                      </select>
                    </div>
                    <div className="form-group"><label>نوع الراتب</label>
                      <select value={formData.salary_type} onChange={(e) => setFormData({...formData, salary_type: e.target.value})}>
                        <option value="monthly">شهري</option>
                        <option value="weekly">أسبوعي</option>
                        <option value="per_order">بالطلب</option>
                        <option value="commission">عمولة</option>
                      </select>
                    </div>
                    <div className="form-group"><label>الراتب الإيرادات (<SAR className="h-[0.8em] w-auto" />)</label><input type="number" value={formData.base_salary} onChange={(e) => setFormData({...formData, base_salary: e.target.value})} placeholder="0.00" min="0" step="0.01" /></div>
                    <div className="form-group"><label>عمولة الطلب (<SAR className="h-[0.8em] w-auto" />)</label><input type="number" value={formData.commission_per_order} onChange={(e) => setFormData({...formData, commission_per_order: e.target.value})} placeholder="0.00" min="0" step="0.01" /></div>
                    <div className="form-group"><label>نسبة العمولة (%)</label><input type="number" value={formData.commission_percentage} onChange={(e) => setFormData({...formData, commission_percentage: e.target.value})} placeholder="0" min="0" max="100" /></div>
                  </div>
                )}

                {/* البيانات البنكية */}
                {activeSection === 'bank' && (
                  <div className="form-grid">
                    <div className="form-group"><label>اسم البنك</label>
                      <select value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})}>
                        <option value="">-- اختر البنك --</option>
                        <option value="الراجحي">مصرف الراجحي</option>
                        <option value="الأهلي">البنك الأهلي</option>
                        <option value="الرياض">بنك الرياض</option>
                        <option value="سامبا">سامبا</option>
                        <option value="الإنماء">مصرف الإنماء</option>
                        <option value="البلاد">بنك البلاد</option>
                        <option value="الجزيرة">بنك الجزيرة</option>
                        <option value="stc_pay">STC Pay</option>
                        <option value="أخرى">أخرى</option>
                      </select>
                    </div>
                    <div className="form-group"><label>رقم الحساب</label><input type="text" value={formData.bank_account} onChange={(e) => setFormData({...formData, bank_account: e.target.value})} placeholder="رقم الحساب البنكي" dir="ltr" /></div>
                    <div className="form-group full-width"><label>رقم IBAN</label><input type="text" value={formData.iban} onChange={(e) => setFormData({...formData, iban: e.target.value.toUpperCase()})} placeholder="SA0000000000000000000000" dir="ltr" maxLength="24" /></div>
                  </div>
                )}

                {/* جهة الاتصال الطوارئ */}
                {activeSection === 'emergency' && (
                  <div className="form-grid">
                    <div className="form-group"><label>اسم جهة الاتصال</label><input type="text" value={formData.emergency_contact_name} onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})} placeholder="اسم الشخص" /></div>
                    <div className="form-group"><label>رقم هاتف الطوارئ</label><input type="tel" value={formData.emergency_contact_phone} onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})} placeholder="05xxxxxxxx" dir="ltr" /></div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">{editingDriver ? 'حفظ التعديلات' : 'إضافة السائق'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal--large {
          max-width: 800px;
          width: 95%;
        }
        .form-tabs {
          display: flex;
          gap: 0;
          border-bottom: 1px solid var(--border);
          overflow-x: auto;
        }
        .form-tab {
          padding: 0.75rem 1.25rem;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-secondary);
          font-size: 0.9rem;
          white-space: nowrap;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }
        .form-tab:hover {
          color: var(--text);
          background: var(--background);
        }
        .form-tab--active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        .form-group.full-width {
          grid-column: 1 / -1;
        }
        @media (max-width: 600px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          .form-tabs {
            flex-wrap: nowrap;
          }
          .form-tab {
            padding: 0.5rem 0.75rem;
            font-size: 0.8rem;
          }
        }
        .header-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        @media (max-width: 768px) {
          .header-actions {
            width: 100%;
          }
          .header-actions .btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default DriversPage;
